import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import DashboardHeader from '../components/common/DashboardHeader';
import {
  Box,
  Typography,
  Button,
  FormControlLabel,
  IconButton,
  InputBase,
  Paper,
  useTheme,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  alpha,
  Menu,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Snackbar,
  Collapse,
  Switch,
  Tooltip,
} from '@mui/material';
import DatePicker from '../shared/components/forms/HotelDatePicker';
import { compareRatePlans, sortRatePlans } from '../utils/ratePlanSort';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TodayIcon from '@mui/icons-material/Today';
import Add from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import EditIcon from '@mui/icons-material/Edit';
import LayersIcon from '@mui/icons-material/Layers';
import BlockIcon from '@mui/icons-material/Block';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import moment from 'moment';
import {
  getRatesData,
  getRateForDate,
  saveRate,
  bulkSaveRates,
  getChannels,
  getConfigurationRatePlans,
} from '../services/api';
import ChannelSelect from '../components/common/ChannelSelect';
import ChannelImage from '../components/common/ChannelImage';
import StatusCellTooltip from '../components/common/StatusCellTooltip';
import { getRateCellTooltip } from '../utils/inventoryRateCellTooltips';
import GroupRateUpdateDrawer from '../components/rates/GroupRateUpdateDrawer';
import GroupRateRestrictionsDrawer from '../components/rates/GroupRateRestrictionsDrawer';
import ManageRateUpdateDrawer from '../components/rates/ManageRateUpdateDrawer';
import RateStopSellDrawer from '../components/rates/RateStopSellDrawer';
import RateSyncDrawer from '../components/rates/RateSyncDrawer';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import { getStaticFileUrl } from '../utils/staticUtils';
import { isMobileShellLayout } from '../utils/mobileShellLayout';
import {
  getDashboardPageShellSx,
  getDashboardPaperSx,
  getDashboardDarkSurfaceTokens,
} from '../utils/dashboardSurfaceSx';
import {
  inventoryVisibleDayCount,
  inventoryGridRowSx,
  inventoryGridScrollInnerSx,
  inventoryStickyLabelCellSx,
  inventoryDateHeaderCellSx,
  inventoryDateHeaderLabelSx,
  inventoryDateHeaderDaySx,
  inventoryMobileToolbarSx,
  inventoryMobileToolbarFilterRowSx,
  inventoryDataCellSx,
  inventoryChannelCornerCellSx,
  inventoryMenuPaperSx,
  inventoryRateCellFillSx,
  inventoryStatusDotSx,
  inventoryWeekendWash,
  isWeekendDate,
} from '../utils/inventoryMobileLayout';
import {
  MobileInventoryGridCorner,
  MobileInventorySkeletonRow,
  InventoryGridSkeleton,
} from '../components/inventory/MobileInventoryGridParts';
import InventoryRoomTypeLabel from '../components/inventory/InventoryRoomTypeLabel';
import ImageIcon from '@mui/icons-material/Image';

const THEME_COLOR = '#4ce1b6';

const pmsMealToken = (plan) => {
  const meal = String(plan?.meal_plan || '').trim().toUpperCase();
  if (meal === 'RO') return 'EP';
  if (meal === 'BB') return 'CP';
  return meal;
};

const roomIdKey = (id) => String(id ?? '');

const mergeRoomIntoListings = (listings, incomingRooms) => {
  const incomingById = new Map((incomingRooms || []).map((room) => [roomIdKey(room.id), room]));
  if (!Array.isArray(listings) || listings.length === 0) {
    return incomingRooms || [];
  }
  const seen = new Set();
  const next = listings.map((room) => {
    const incoming = incomingById.get(roomIdKey(room.id));
    if (!incoming) return room;
    seen.add(roomIdKey(room.id));
    return {
      ...room,
      ...incoming,
      rate_plans: { ...(room.rate_plans || {}), ...(incoming.rate_plans || {}) },
    };
  });
  incomingById.forEach((room, id) => {
    if (!seen.has(id)) next.push(room);
  });
  return next;
};

const mergeChannelListings = (current, incoming) => {
  const next = { ...(current || {}) };
  Object.entries(incoming || {}).forEach(([channelId, rooms]) => {
    next[channelId] = mergeRoomIntoListings(next[channelId], rooms);
  });
  return next;
};

const collectRatePlansFromRooms = (rooms, existing = []) => {
  const byId = new Map();
  (existing || []).forEach((plan) => {
    if (plan?.id) byId.set(Number(plan.id), plan);
  });
  (rooms || []).forEach((room) => {
    Object.values(room?.rate_plans || {}).forEach((plan) => {
      const id = Number(plan?.rate_plan_id ?? plan?.id);
      if (!Number.isFinite(id) || id <= 0) return;
      byId.set(id, {
        ...(byId.get(id) || {}),
        id,
        name: plan.rate_plan_name || plan.name,
        code: plan.rate_plan_code || plan.code,
        sort_order: plan.sort_order ?? 0,
        meal_plan: plan.meal_plan || plan.mealPlan || '',
        is_pms_enabled: plan.is_pms_enabled ?? plan.enablePMS,
        is_channel_manager_enabled: plan.is_channel_manager_enabled ?? plan.enableChannelManager,
      });
    });
  });
  return sortRatePlans(Array.from(byId.values()));
};

const ManageRatePage = () => {
  // Initialize theme synchronously to prevent flash
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const themeColors = getDashboardDarkSurfaceTokens(isDark);
  const theme = useTheme(); // Use MUI theme for palette access
  const isMobileLayout = isMobileShellLayout();
  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });

    return cleanup;
  }, []);
  const [listings, setListings] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [updateDrawerContext, setUpdateDrawerContext] = useState(null);
  const [groupDrawerOpen, setGroupDrawerOpen] = useState(false);
  const [restrictionsDrawerOpen, setRestrictionsDrawerOpen] = useState(false);
  const [stopSellDrawerOpen, setStopSellDrawerOpen] = useState(false);
  const [stopSellInitialContext, setStopSellInitialContext] = useState(null);
  const [syncDrawerOpen, setSyncDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [calendarStartDate, setCalendarStartDate] = useState(moment()); // Selected date for calendar start (default: current date)
  const [dateOffset, setDateOffset] = useState(0); // Days offset from today (calculated from calendarStartDate)
  const [selectedRoomTypeFilter, setSelectedRoomTypeFilter] = useState('all'); // 'all' or room type ID
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Track initial load to prevent showing red before data loads
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false); // Track if we've loaded data at least once
  const [isRateGridLoading, setIsRateGridLoading] = useState(true); // Only rate grid (daily rate boxes) loading, not header/date/channel
  const cachedRatesRef = useRef({}); // Cache rates by date range using ref to avoid dependency issues
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState('all'); // Selected channel filter (default: All Channels)
  const [ratePlans, setRatePlans] = useState([]); // Grid rate plans (channel-dependent)
  const [pmsRatePlans, setPmsRatePlans] = useState([]); // PMS meal plans for Manage Rates
  const [channelManagerRatePlans, setChannelManagerRatePlans] = useState([]);
  const [allChannelsRates, setAllChannelsRates] = useState({}); // Rates for all channels when "All Channels" is selected
  const [expandedChannels, setExpandedChannels] = useState({ pms: false, combined: true }); // PMS closed; All Channels open by default
  const [expandedRoomTypes, setExpandedRoomTypes] = useState({}); // `${channelKey}|${roomTypeId}` → expanded rate-plan rows
  const [loadedRoomIds, setLoadedRoomIds] = useState(() => new Set());
  const [loadingRoomIds, setLoadingRoomIds] = useState(() => new Set());
  const loadedRoomIdsRef = useRef(new Set());
  const [hydratedSources, setHydratedSources] = useState(() => new Set());
  const loadGenerationRef = useRef(0);
  const ratePlanCatalogPromiseRef = useRef(null);
  const [channelView, setChannelView] = useState(false); // ON: per-OTA rows; OFF: PMS + All Channels (both expanded)
  const [snackbarMessage, setSnackbarMessage] = useState(null); // e.g. past date clicked
  const [isRefreshingRates, setIsRefreshingRates] = useState(false);
  
  // State for Action dropdown menu
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);

  const hasChannels = Array.isArray(channels) && channels.length > 0;
  const manageRatePlans = pmsRatePlans.length > 0 ? pmsRatePlans : ratePlans;
  const updateDrawerRooms = useMemo(
    () =>
      (Array.isArray(listings) ? listings : []).map((room) => ({
        id: room.id,
        name: room.name,
      })),
    [listings]
  );
  const stopSellRatePlans = useMemo(() => {
    const byId = new Map();
    [
      ...(Array.isArray(ratePlans) ? ratePlans : []),
      ...(Array.isArray(pmsRatePlans) ? pmsRatePlans : []),
      ...(Array.isArray(channelManagerRatePlans) ? channelManagerRatePlans : []),
    ]
      .forEach((plan) => {
        if (!plan?.id) return;
        byId.set(plan.id, { ...(byId.get(plan.id) || {}), ...plan });
      });
    return sortRatePlans(Array.from(byId.values()));
  }, [ratePlans, pmsRatePlans, channelManagerRatePlans]);

  // Inline click-to-edit (All Channels / rate cells)
  const [inlineEdit, setInlineEdit] = useState(null); // { key, channelId, dateStr, roomTypeId, ratePlanId, original }
  const [inlineEditValue, setInlineEditValue] = useState('');
  const [savingCellKeys, setSavingCellKeys] = useState(() => new Set());

  // Constants
  const visibleDatesCount = inventoryVisibleDayCount(isMobileLayout);
  const DATE_BUFFER = 0; // Fetch only currently visible calendar dates
  
  // Helper function to get date range for API calls
  const getDateRange = useCallback((offset, buffer = DATE_BUFFER) => {
    const baseDate = moment().startOf('day');
    const start = baseDate.clone().add(offset - buffer, 'days').format('YYYY-MM-DD');
    const end = baseDate
      .clone()
      .add(offset + (visibleDatesCount - 1) + buffer, 'days')
      .format('YYYY-MM-DD');
    return { start, end };
  }, [visibleDatesCount]);
  
  // Calculate dateOffset from calendarStartDate
  React.useEffect(() => {
    const offset = calendarStartDate.clone().startOf('day').diff(moment().startOf('day'), 'days');
    setDateOffset(offset);
  }, [calendarStartDate]);
  
  // Generate dates for the calendar view (visible dates from selected start date)
  const dates = React.useMemo(() => {
    const dates = [];
    const start = calendarStartDate.clone();
    const end = calendarStartDate.clone().add(visibleDatesCount - 1, 'days');
    let current = start.clone();
    
    while (current.isSameOrBefore(end, 'day')) {
      dates.push(current.clone());
      current.add(1, 'day');
    }
    
    return dates;
  }, [calendarStartDate, visibleDatesCount]);

  const markRoomsLoaded = useCallback((roomIds) => {
    const keys = (roomIds || []).map(roomIdKey).filter(Boolean);
    if (!keys.length) return;
    setLoadedRoomIds((prev) => {
      const next = new Set(prev);
      keys.forEach((key) => next.add(key));
      loadedRoomIdsRef.current = next;
      return next;
    });
  }, []);

  const markSourcesHydrated = useCallback((sourceKeys) => {
    const keys = (Array.isArray(sourceKeys) ? sourceKeys : [sourceKeys])
      .map((key) => String(key ?? ''))
      .filter(Boolean);
    if (!keys.length) return;
    setHydratedSources((prev) => {
      let changed = false;
      const next = new Set(prev);
      keys.forEach((key) => {
        if (!next.has(key)) {
          next.add(key);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, []);

  const isRatesPending = useCallback((sourceKey) => {
    if (sourceKey === 'combined') {
      if (hydratedSources.has('combined')) return false;
      return ![...hydratedSources].some((key) => key !== 'pms');
    }
    if (sourceKey == null || sourceKey === '') {
      return hydratedSources.size === 0;
    }
    return !hydratedSources.has(String(sourceKey));
  }, [hydratedSources]);

  const setRoomLoading = useCallback((roomTypeId, isLoading) => {
    const key = roomIdKey(roomTypeId);
    if (!key) return;
    setLoadingRoomIds((prev) => {
      const next = new Set(prev);
      if (isLoading) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const applyRoomPlanCatalog = useCallback((rooms) => {
    setRatePlans((prev) => collectRatePlansFromRooms(rooms, prev));
  }, []);

  const hydrateListings = useCallback((rooms) => {
    const rows = rooms || [];
    markRoomsLoaded(rows.map((room) => room.id));
    applyRoomPlanCatalog(rows);
    return rows;
  }, [applyRoomPlanCatalog, markRoomsLoaded]);

  const fetchRates = useCallback(async (offset = dateOffset, showLoading = true, channelId = selectedChannel, { isStale = () => false } = {}) => {
    if (channelId === 'all') {
      return { success: false, reason: 'all_channels' };
    }

    const cacheKey = `${offset}-${offset + visibleDatesCount}-${channelId}`;
    if (cachedRatesRef.current[cacheKey]) {
      const cachedData = cachedRatesRef.current[cacheKey];
      const rooms = cachedData.listings || [];
      setListings(rooms);
      hydrateListings(rooms);
      markSourcesHydrated(channelId);
      setIsInitialLoad(false);
      setHasLoadedOnce(true);
      return { success: true, cached: true };
    }

    if (showLoading) {
      setLoading(true);
    }
    setIsInitialLoad(true);

    try {
      const { start, end } = getDateRange(offset);
      try {
        const shells = await getRatesData(start, end, channelId, { includeRatePlans: false });
        if (!isStale() && Array.isArray(shells?.listings)) {
          setListings(shells.listings);
          setIsRateGridLoading(false);
        }
      } catch (shellError) {
        // Fall through to the full rates request.
      }
      if (isStale()) return { success: false, stale: true };

      const data = await getRatesData(start, end, channelId);
      if (isStale()) return { success: false, stale: true };
      cachedRatesRef.current[cacheKey] = data;
      const rooms = data.listings || [];
      setListings(rooms);
      hydrateListings(rooms);
      markSourcesHydrated(channelId);
      setIsInitialLoad(false);
      setHasLoadedOnce(true);
      return { success: true, cached: false };
    } catch (error) {
      if (!isStale()) {
        setListings([]);
        setIsInitialLoad(false);
        setHasLoadedOnce(true);
      }
      return { success: false, error };
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [dateOffset, selectedChannel, getDateRange, hydrateListings, markSourcesHydrated, visibleDatesCount]);

  useEffect(() => {
    let cancelled = false;
    const fetchChannelData = async () => {
      try {
        const channelsData = await getChannels();
        if (cancelled) return;
        const channelsList = channelsData.channels || channelsData || [];
        setChannels(channelsList);
        if (channelsList.length === 0 && selectedChannel === 'all') {
          setSelectedChannel('pms');
          setChannelView(false);
        }
      } catch (error) {
        if (!cancelled) setChannels([]);
      }
    };
    fetchChannelData();
    return () => { cancelled = true; };
    // Load channel list once; room rates load separately per room type.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllChannelRates = useCallback(async (offset = dateOffset, { bypassCache = false, isStale = () => false } = {}) => {
    const cacheKey = `${offset}-${offset + visibleDatesCount}-all`;
    if (!bypassCache && cachedRatesRef.current[cacheKey]) {
      const cached = cachedRatesRef.current[cacheKey];
      const channelListings = cached?.channel_listings || cached || {};
      const pmsListings = channelListings.pms || cached?.listings || [];
      setAllChannelsRates(channelListings);
      setListings(pmsListings);
      Object.values(channelListings).forEach((rooms) => {
        if (Array.isArray(rooms)) hydrateListings(rooms);
      });
      markSourcesHydrated([...Object.keys(channelListings), 'combined']);
      setIsInitialLoad(false);
      setHasLoadedOnce(true);
      return { success: true, cached: true };
    }

    const applyChannelPayload = (data, { replaceAll = false } = {}) => {
      const channelListings = data?.channel_listings;
      if (channelListings && typeof channelListings === 'object') {
        setAllChannelsRates((prev) => (
          replaceAll ? channelListings : mergeChannelListings(prev, channelListings)
        ));
        Object.values(channelListings).forEach((rooms) => {
          if (Array.isArray(rooms)) hydrateListings(rooms);
        });
        markSourcesHydrated(Object.keys(channelListings));
        if (Object.keys(channelListings).some((key) => key !== 'pms')) {
          markSourcesHydrated('combined');
        }
      }
      const pmsListings = channelListings?.pms || data?.listings || [];
      if (Array.isArray(pmsListings) && pmsListings.length) {
        setListings(pmsListings);
        hydrateListings(pmsListings);
        if (!channelListings) {
          setAllChannelsRates((prev) => ({ ...prev, pms: pmsListings }));
          markSourcesHydrated('pms');
        }
      }
    };

    try {
      const { start, end } = getDateRange(offset);
      try {
        const shells = await getRatesData(start, end, 'all', { includeRatePlans: false });
        if (!isStale() && shells) {
          const shellListings = shells.channel_listings || {};
          setAllChannelsRates(shellListings);
          setListings(shellListings.pms || shells.listings || []);
          setIsRateGridLoading(false);
        }
      } catch (shellError) {
        // Fall through to rate-value requests.
      }
      if (isStale()) return { success: false, stale: true };

      const pmsPromise = getRatesData(start, end, 'pms')
        .then((data) => {
          if (!isStale()) applyChannelPayload(data);
        })
        .catch(() => {});

      const allPromise = getRatesData(start, end, 'all')
        .then((data) => {
          if (isStale()) return;
          cachedRatesRef.current[cacheKey] = data;
          applyChannelPayload(data, { replaceAll: true });
        });

      await Promise.all([pmsPromise, allPromise]);
      if (isStale()) return { success: false, stale: true };
      setIsInitialLoad(false);
      setHasLoadedOnce(true);
      return { success: true, cached: false };
    } catch (error) {
      if (!isStale()) {
        setIsInitialLoad(false);
        setHasLoadedOnce(true);
        setAllChannelsRates({});
        setListings([]);
      }
      return { success: false, error };
    }
  }, [dateOffset, getDateRange, hydrateListings, markSourcesHydrated, visibleDatesCount]);

  const fetchRoomRates = useCallback(async (roomTypeId, { bypassCache = false } = {}) => {
    const roomKey = roomIdKey(roomTypeId);
    if (!roomKey) return { success: false };
    const channelId = selectedChannel === 'all' ? 'all' : selectedChannel;
    const cacheKey = `${dateOffset}-${dateOffset + visibleDatesCount}-${channelId}-room-${roomKey}`;
    setRoomLoading(roomKey, true);
    try {
      if (!bypassCache && cachedRatesRef.current[cacheKey]) {
        const cached = cachedRatesRef.current[cacheKey];
        if (channelId === 'all') {
          const channelListings = cached?.channel_listings || {};
          setAllChannelsRates((prev) => mergeChannelListings(prev, channelListings));
          const rooms = channelListings.pms || cached?.listings || [];
          setListings((prev) => mergeRoomIntoListings(prev, rooms));
          applyRoomPlanCatalog(rooms);
        } else {
          const rooms = cached?.listings || [];
          setListings((prev) => mergeRoomIntoListings(prev, rooms));
          applyRoomPlanCatalog(rooms);
        }
        markRoomsLoaded([roomKey]);
        return { success: true, cached: true };
      }

      const { start, end } = getDateRange(dateOffset);
      const data = await getRatesData(start, end, channelId, { roomTypeId: roomKey });
      cachedRatesRef.current[cacheKey] = data;
      if (channelId === 'all') {
        const channelListings = data?.channel_listings || {};
        setAllChannelsRates((prev) => mergeChannelListings(prev, channelListings));
        const rooms = channelListings.pms || data?.listings || [];
        setListings((prev) => mergeRoomIntoListings(prev, rooms));
        applyRoomPlanCatalog(rooms);
      } else {
        const rooms = data?.listings || [];
        setListings((prev) => mergeRoomIntoListings(prev, rooms));
        applyRoomPlanCatalog(rooms);
      }
      markRoomsLoaded([roomKey]);
      return { success: true, cached: false };
    } catch (error) {
      return { success: false, error };
    } finally {
      setRoomLoading(roomKey, false);
    }
  }, [applyRoomPlanCatalog, dateOffset, getDateRange, markRoomsLoaded, selectedChannel, setRoomLoading, visibleDatesCount]);

  const refreshLoadedRates = useCallback(async () => {
    cachedRatesRef.current = {};
    if (selectedChannel === 'all') {
      await fetchAllChannelRates(dateOffset, { bypassCache: true });
    } else {
      await fetchRates(dateOffset, false, selectedChannel);
    }
  }, [dateOffset, fetchAllChannelRates, fetchRates, selectedChannel]);

  const handleRefreshRates = useCallback(async () => {
    if (isRefreshingRates || loading || isTransitioning) return;
    setIsRefreshingRates(true);
    setHydratedSources(new Set());
    setIsInitialLoad(true);
    if (
      (selectedChannel === 'all' && Object.keys(allChannelsRates).length === 0)
      || (selectedChannel !== 'all' && listings.length === 0)
    ) {
      setIsRateGridLoading(true);
    }
    ratePlanCatalogPromiseRef.current = null;
    try {
      await Promise.all([
        refreshLoadedRates(),
        getConfigurationRatePlans({ platform: 'channel_manager' })
          .then((plans) => setChannelManagerRatePlans(sortRatePlans(plans || [])))
          .catch(() => {}),
        getConfigurationRatePlans({ platform: 'pms' })
          .then((plans) => setPmsRatePlans(sortRatePlans(plans || [])))
          .catch(() => {}),
      ]);
    } catch (error) {
      setSnackbarMessage('Could not refresh rates');
    } finally {
      setIsRefreshingRates(false);
      setIsRateGridLoading(false);
      setIsInitialLoad(false);
      setHasLoadedOnce(true);
    }
  }, [allChannelsRates, isRefreshingRates, isTransitioning, listings.length, loading, refreshLoadedRates, selectedChannel]);

  const ensureRatePlanCatalogs = useCallback(async () => {
    if (pmsRatePlans.length > 0 && channelManagerRatePlans.length > 0) {
      return;
    }
    if (!ratePlanCatalogPromiseRef.current) {
      ratePlanCatalogPromiseRef.current = Promise.all([
        getConfigurationRatePlans({ platform: 'channel_manager' }),
        getConfigurationRatePlans({ platform: 'pms' }),
      ]).then(([cmRatePlansData, pmsRatePlansData]) => {
        setChannelManagerRatePlans(sortRatePlans(cmRatePlansData || []));
        setPmsRatePlans(sortRatePlans(pmsRatePlansData || []));
      }).catch(() => {
        ratePlanCatalogPromiseRef.current = null;
      });
    }
    await ratePlanCatalogPromiseRef.current;
  }, [channelManagerRatePlans.length, pmsRatePlans.length]);
  
  // Channel / date change: paint room shells first, then fill rates (cell skeletons until then).
  useEffect(() => {
    const generation = ++loadGenerationRef.current;
    const isStale = () => loadGenerationRef.current !== generation;
    const hasExistingData =
      selectedChannel === 'all'
        ? Object.keys(allChannelsRates).length > 0
        : listings.length > 0;
    setHydratedSources(new Set());
    setIsInitialLoad(true);
    if (!hasExistingData) {
      setIsRateGridLoading(true);
    } else {
      setIsTransitioning(true);
    }

    setLoadedRoomIds(new Set());
    loadedRoomIdsRef.current = new Set();

    const loadChannelRates = async () => {
      try {
        if (selectedChannel === 'all') {
          await fetchAllChannelRates(dateOffset, { isStale });
        } else {
          await fetchRates(dateOffset, false, selectedChannel, { isStale });
        }
      } catch (error) {
        if (!isStale()) {
          setIsInitialLoad(false);
          setHasLoadedOnce(true);
        }
      } finally {
        if (!isStale()) {
          setIsRateGridLoading(false);
          setIsInitialLoad(false);
          setHasLoadedOnce(true);
          requestAnimationFrame(() => {
            setIsTransitioning(false);
          });
        }
      }
    };

    loadChannelRates();
    return () => { loadGenerationRef.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannel, dateOffset]);

  // Handle date cell click — opens range Update drawer (Action menu / rate-plan extras)
  const handleDateClick = async (roomTypeId, date, channelId = null, ratePlanId = null) => {
    const clickedDate = moment(date);
    if (clickedDate.isBefore(moment().startOf('day'), 'day')) {
      setSnackbarMessage('Cannot set rates for past dates');
      return;
    }
    const dateStr = date.format('YYYY-MM-DD');
    setSelectedDates([{ start: dateStr, end: dateStr }]);

    const pmsPlans = pmsRatePlans.length > 0 ? pmsRatePlans : ratePlans;
    const clickedId = ratePlanId != null ? Number(ratePlanId) : null;
    const clickedIsPms = pmsPlans.some((plan) => Number(plan.id) === clickedId);
    let planIdToUse = clickedIsPms ? clickedId : null;
    if (planIdToUse == null && clickedId != null) {
      const clickedPlan = [...pmsPlans, ...channelManagerRatePlans].find(
        (plan) => Number(plan.id) === clickedId
      );
      const meal = pmsMealToken(clickedPlan);
      const mealMatch = pmsPlans.find((plan) => pmsMealToken(plan) === meal);
      planIdToUse = mealMatch?.id ?? pmsPlans[0]?.id ?? null;
    }
    if (planIdToUse == null) {
      planIdToUse = pmsPlans[0]?.id ?? null;
    }

    ensureRatePlanCatalogs();
    setUpdateDrawerContext({
      date: dateStr,
      roomTypeId,
      ratePlanId: planIdToUse,
      channelId: channelId || selectedChannel,
      empty: false,
    });
    setDrawerOpen(true);
  };

  const makeRateCellKey = (channelId, dateStr, roomTypeId, ratePlanId) =>
    `${channelId ?? 'pms'}|${dateStr}|${roomTypeId}|${ratePlanId ?? ''}`;

  const patchLocalRateCell = useCallback(
    (channelIdKeys, dateStr, roomTypeId, ratePlanId, baseRate) => {
      const applyToListing = (listing) => {
        if (String(listing.id) !== String(roomTypeId)) return listing;
        const next = { ...listing };
        if (next.rate_plans && typeof next.rate_plans === 'object') {
          const plans = { ...next.rate_plans };
          Object.keys(plans).forEach((planKey) => {
            const plan = plans[planKey];
            if (
              ratePlanId != null
              && String(plan.rate_plan_id) !== String(ratePlanId)
              && String(planKey) !== String(ratePlanId)
            ) {
              return;
            }
            const rates = { ...(plan.rates || {}) };
            rates[dateStr] = {
              ...(rates[dateStr] || {}),
              base_rate: baseRate,
              available: rates[dateStr]?.available !== false,
            };
            plans[planKey] = { ...plan, rates };
          });
          next.rate_plans = plans;
        } else if (next.rates) {
          next.rates = {
            ...next.rates,
            [dateStr]: {
              ...(next.rates[dateStr] || {}),
              base_rate: baseRate,
              available: next.rates[dateStr]?.available !== false,
            },
          };
        }
        return next;
      };

      if (selectedChannel === 'all') {
        setAllChannelsRates((prev) => {
          const next = { ...prev };
          channelIdKeys.forEach((chKey) => {
            const list = next[chKey];
            if (!Array.isArray(list)) return;
            next[chKey] = list.map(applyToListing);
          });
          return next;
        });
      } else {
        setListings((prev) => (Array.isArray(prev) ? prev.map(applyToListing) : prev));
      }
      cachedRatesRef.current = {};
    },
    [selectedChannel]
  );

  const beginInlineRateEdit = ({
    channelId,
    date,
    roomTypeId,
    ratePlanId,
    currentRate,
    keyPrefix = '',
  }) => {
    // Stop sell / blocked rates: open Stop Sell drawer instead of inline edit
    const isStopSell = !!(currentRate && (currentRate.stop_sell || currentRate.stopSell));
    const isBlocked = !!(currentRate && currentRate.available === false);
    if (isStopSell || isBlocked) {
      const dateStr = moment(date).format('YYYY-MM-DD');
      const resolvedRoomTypeId =
        roomTypeId != null && String(roomTypeId) !== '' && String(roomTypeId) !== 'all'
          ? String(roomTypeId)
          : (listings[0]?.id != null ? String(listings[0].id) : null);
      const planId =
        ratePlanId != null && ratePlanId !== ''
          ? ratePlanId
          : (ratePlans.length > 0 ? ratePlans[0].id : null);
      if (!resolvedRoomTypeId || planId == null || planId === '') {
        setSnackbarMessage('Cannot open stop sell — missing room type or meal plan');
        return;
      }
      setStopSellInitialContext({
        date: dateStr,
        roomTypeId: resolvedRoomTypeId,
        ratePlanId: planId,
        channelId: channelId || selectedChannel,
        action: isStopSell ? 'clear' : 'apply',
      });
      ensureRatePlanCatalogs();
      setStopSellDrawerOpen(true);
      return;
    }

    const clickedDate = moment(date);
    if (clickedDate.isBefore(moment().startOf('day'), 'day')) {
      setSnackbarMessage('Cannot set rates for past dates');
      return;
    }
    const dateStr = moment(date).format('YYYY-MM-DD');
    const resolvedRoomTypeId =
      roomTypeId != null && String(roomTypeId) !== '' && String(roomTypeId) !== 'all'
        ? String(roomTypeId)
        : (listings[0]?.id != null ? String(listings[0].id) : null);
    const planId =
      ratePlanId != null && ratePlanId !== ''
        ? ratePlanId
        : (ratePlans.length > 0 ? ratePlans[0].id : null);
    if (!resolvedRoomTypeId) {
      setSnackbarMessage('Select a room type before editing rates');
      return;
    }
    if (planId == null || planId === '') {
      setSnackbarMessage('No meal plan available to edit this rate');
      return;
    }
    const realKey = makeRateCellKey(channelId, dateStr, resolvedRoomTypeId, planId);
    if (savingCellKeys.has(realKey)) return;
    const key = `${keyPrefix}${realKey}`;
    const roundedOriginal =
      currentRate?.base_rate != null && currentRate?.base_rate !== ''
        ? Math.round(Number(currentRate.base_rate))
        : '';
    setInlineEdit({
      key,
      channelId: channelId == null || channelId === 'pms' ? 'pms' : String(channelId),
      dateStr,
      roomTypeId: resolvedRoomTypeId,
      ratePlanId: planId,
      original: roundedOriginal === '' ? '' : roundedOriginal,
    });
    setInlineEditValue(roundedOriginal === '' ? '' : String(roundedOriginal));
  };

  const cancelInlineRateEdit = () => {
    setInlineEdit(null);
    setInlineEditValue('');
  };

  const commitInlineRateEdit = async () => {
    if (!inlineEdit) return;
    const { key, channelId, dateStr, roomTypeId, ratePlanId, original } = inlineEdit;
    const trimmed = String(inlineEditValue ?? '').trim();
    const originalRounded =
      original === '' || original == null ? '' : Math.round(Number(original));
    if (trimmed === '' || trimmed === String(originalRounded)) {
      cancelInlineRateEdit();
      return;
    }
    const baseRate = Math.round(Number(trimmed));
    if (!Number.isFinite(baseRate) || baseRate < 0) {
      setSnackbarMessage('Enter a valid rate');
      return;
    }

    const isPms = channelId === 'pms';
    const otaChannels = Array.isArray(channels) ? channels : [];
    // Aiosell rate-push is one rate for all OTAs — always write every OTA ChannelRate
    const targetChannelIds = isPms
      ? ['pms']
      : (otaChannels.length > 0
          ? otaChannels.map((ch) => String(ch.id))
          : [String(channelId)]);

    if (!isPms && targetChannelIds.length === 0) {
      setSnackbarMessage('No OTA channels to update');
      cancelInlineRateEdit();
      return;
    }

    const keysToSave = new Set(
      targetChannelIds.map((cid) => makeRateCellKey(cid, dateStr, roomTypeId, ratePlanId))
    );
    setSavingCellKeys((prev) => new Set([...prev, ...keysToSave]));
    setInlineEdit(null);
    setInlineEditValue('');

    const previousBase = original;
    try {
      let extras = {
        single_occupancy_rate: null,
        extra_adult_rate: 0,
        extra_child_rate: 0,
        available: true,
        currency: 'INR',
      };
      try {
        const fetched = await getRateForDate(
          dateStr,
          roomTypeId,
          ratePlanId,
          isPms ? 'pms' : targetChannelIds[0]
        );
        extras = {
          single_occupancy_rate: fetched.single_occupancy_rate ?? null,
          extra_adult_rate: fetched.extra_adult_rate ?? 0,
          extra_child_rate: fetched.extra_child_rate ?? 0,
          available: fetched.available !== false,
          currency: fetched.currency || 'INR',
          min_los: fetched.min_los || null,
          max_los: fetched.max_los || null,
        };
      } catch {
        // use defaults
      }

      const updates = targetChannelIds.map((cid) => ({
        date: dateStr,
        room_type_id: roomTypeId,
        rate_plan_id: ratePlanId,
        channel_id: cid,
        base_rate: baseRate,
        ...extras,
      }));

      if (updates.length === 1) {
        await saveRate(updates[0]);
      } else {
        await bulkSaveRates(updates);
      }

      patchLocalRateCell(
        targetChannelIds.map((cid) => (cid === 'pms' ? 'pms' : String(cid))),
        dateStr,
        roomTypeId,
        ratePlanId,
        baseRate
      );
      setSnackbarMessage(
        isPms
          ? 'PMS rate saved'
          : 'Rate saved · syncing all OTAs'
      );
    } catch (error) {
      setSnackbarMessage(
        error?.response?.data?.error
          || error?.message
          || 'Failed to save rate'
      );
      if (previousBase !== '' && previousBase != null) {
        patchLocalRateCell(
          targetChannelIds.map((cid) => (cid === 'pms' ? 'pms' : String(cid))),
          dateStr,
          roomTypeId,
          ratePlanId,
          Number(previousBase)
        );
      }
    } finally {
      setSavingCellKeys((prev) => {
        const next = new Set(prev);
        keysToSave.forEach((k) => next.delete(k));
        return next;
      });
    }
  };

  // Filter listings by selected room type ('all' = every room type)
  const filteredListings = selectedRoomTypeFilter && selectedRoomTypeFilter !== 'all'
    ? listings.filter((listing) => listing.id.toString() === selectedRoomTypeFilter)
    : listings;

  // Ensure a valid room type filter once listings arrive
  useEffect(() => {
    if (!selectedRoomTypeFilter && listings.length > 0) {
      setSelectedRoomTypeFilter('all');
    }
  }, [listings, selectedRoomTypeFilter]);

  // Prepare channels data for display when "All Channels" is selected
  const pmsChannelMeta = React.useMemo(
    () => ({ id: 'pms', name: 'PMS', image_path: getStaticFileUrl('ota/pms.png') }),
    []
  );
  const displayChannels = React.useMemo(() => {
    if (selectedChannel !== 'all') return [];
    if (!hasChannels) return [pmsChannelMeta];
    // Channel View OFF: combined All Channels row; ON: every OTA as its own row
    if (!channelView) {
      return [
        pmsChannelMeta,
        {
          id: 'combined',
          name: 'All Channels',
          isCombined: true,
        },
      ];
    }
    return [pmsChannelMeta, ...(Array.isArray(channels) ? channels : [])];
  }, [selectedChannel, hasChannels, channelView, channels, pmsChannelMeta]);

  const mergeOtaChannelListings = useCallback((channelListings, otaChannels) => {
    const listingsByChannel = channelListings && typeof channelListings === 'object' ? channelListings : {};
    const orderedIds = (Array.isArray(otaChannels) ? otaChannels : [])
      .map((channel) => String(channel?.id ?? ''))
      .filter((id) => id && id !== 'pms' && id !== 'all');
    const extraIds = Object.keys(listingsByChannel).filter(
      (id) => id && id !== 'pms' && !orderedIds.includes(id)
    );
    const roomsById = new Map();
    [...orderedIds, ...extraIds].forEach((channelId) => {
      const listings = listingsByChannel[channelId];
      if (!Array.isArray(listings)) return;
      listings.forEach((room) => {
        const roomKey = String(room.id);
        if (!roomsById.has(roomKey)) {
          roomsById.set(roomKey, { ...room, rate_plans: {} });
        }
        const target = roomsById.get(roomKey);
        Object.entries(room.rate_plans || {}).forEach(([planKey, plan]) => {
          const current = target.rate_plans[planKey] || { ...plan, rates: {} };
          const mergedRates = { ...(current.rates || {}) };
          Object.entries(plan?.rates || {}).forEach(([dateStr, day]) => {
            const hasValue = day && (day.base_rate != null || day.final_rate != null);
            const existing = mergedRates[dateStr];
            const existingHasValue = existing && (existing.base_rate != null || existing.final_rate != null);
            if (!existingHasValue && (hasValue || !existing)) {
              mergedRates[dateStr] = day;
            }
          });
          target.rate_plans[planKey] = { ...current, ...plan, rates: mergedRates };
        });
      });
    });
    return Array.from(roomsById.values());
  }, []);

  const getPrimaryRateBundle = useCallback((channelRates, roomTypeId) => {
    const roomTypeData = (channelRates || []).find(
      (r) => r.id.toString() === String(roomTypeId)
    );
    let rates = {};
    let primaryRatePlanId = ratePlans.length > 0 ? ratePlans[0].id : null;
    if (roomTypeData?.rate_plans && typeof roomTypeData.rate_plans === 'object') {
      const ratePlansArray = sortRatePlans(Object.values(roomTypeData.rate_plans));
      if (ratePlansArray.length > 0) {
        rates = ratePlansArray[0].rates || {};
        primaryRatePlanId =
          ratePlansArray[0].rate_plan_id
          ?? ratePlansArray[0].id
          ?? Object.keys(roomTypeData.rate_plans)[0]
          ?? primaryRatePlanId;
      } else {
        const firstKey = Object.keys(roomTypeData.rate_plans)[0];
        if (firstKey != null) primaryRatePlanId = primaryRatePlanId ?? firstKey;
      }
    } else if (roomTypeData?.rates) {
      rates = roomTypeData.rates;
    }
    if (primaryRatePlanId != null) {
      primaryRatePlanId = Number(primaryRatePlanId);
      if (Number.isNaN(primaryRatePlanId)) {
        primaryRatePlanId = ratePlans.length > 0 ? ratePlans[0].id : null;
      }
    }
    return { rates, primaryRatePlanId, roomTypeData };
  }, [ratePlans]);

  /** Every rate plan (meal plan) for a room type, with its own daily rates. */
  const getRatePlanRows = useCallback((channelRates, roomTypeId) => {
    const roomTypeData = (channelRates || []).find(
      (r) => r.id.toString() === String(roomTypeId)
    );
    const plansObj = roomTypeData?.rate_plans;
    if (plansObj && typeof plansObj === 'object' && Object.keys(plansObj).length > 0) {
      return Object.entries(plansObj)
        .map(([planKey, plan]) => {
          const planId = Number(plan?.rate_plan_id ?? plan?.id ?? planKey);
          const fallbackPlan = ratePlans.find((rp) => Number(rp.id) === planId);
          const code = String(
            plan?.rate_plan_code || fallbackPlan?.code || ''
          ).trim();
          return {
            ratePlanId: Number.isNaN(planId) ? null : planId,
            name: plan?.rate_plan_name || fallbackPlan?.name || `Plan ${planId}`,
            code,
            sort_order: plan?.sort_order ?? fallbackPlan?.sort_order ?? 0,
            meal_plan: plan?.meal_plan || fallbackPlan?.meal_plan || '',
            rates: plan?.rates || {},
          };
        })
        .filter((p) => p.ratePlanId != null)
        .sort(compareRatePlans);
    }
    return [];
  }, [ratePlans]);

  const toggleRoomTypeExpand = useCallback((roomTypeKey) => {
    setExpandedRoomTypes((prev) => ({
      ...prev,
      [roomTypeKey]: !prev[roomTypeKey],
    }));
  }, []);

  const handleRoomTypeActivate = useCallback(async (roomTypeKey, roomTypeId) => {
    const key = roomIdKey(roomTypeId);
    const alreadyExpanded = Boolean(expandedRoomTypes[roomTypeKey]);
    if (alreadyExpanded) {
      toggleRoomTypeExpand(roomTypeKey);
      return;
    }
    setExpandedRoomTypes((prev) => ({ ...prev, [roomTypeKey]: true }));
    if (!loadedRoomIds.has(key) && !loadingRoomIds.has(key)) {
      await fetchRoomRates(key);
    }
  }, [expandedRoomTypes, fetchRoomRates, loadedRoomIds, loadingRoomIds, toggleRoomTypeExpand]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '—';
    return `₹${parseFloat(value).toFixed(0)}`;
  };

  const rateAmountTextSx = {
    fontWeight: 700,
    fontSize: isMobileLayout ? '0.72rem' : '0.875rem',
    lineHeight: 1.1,
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: isMobileLayout ? '-0.025em' : 0,
    width: '100%',
    maxWidth: '100%',
    textAlign: 'center',
    display: 'block',
  };

  /** Short label for dense grid: "Expedia Affiliate Network" → "Expedia" */
  const shortChannelName = useCallback((name) => {
    const raw = String(name || '').trim();
    if (!raw) return '';
    return raw.split(/\s+/)[0];
  }, []);

  const toggleChannelExpand = useCallback((channelKey) => {
    setExpandedChannels((prev) => ({
      ...prev,
      [channelKey]: !prev[channelKey],
    }));
  }, []);

  useEffect(() => {
    setExpandedRoomTypes({});
    if (selectedChannel !== 'all' || selectedRoomTypeFilter !== 'all') {
      setExpandedChannels({});
      return;
    }
    // Combined mode: keep PMS + All Channels expanded by default
    if (!channelView) {
      setExpandedChannels({ pms: false, combined: true });
    } else {
      setExpandedChannels({});
    }
  }, [selectedChannel, selectedRoomTypeFilter, channelView]);

  useEffect(() => {
    if (!selectedRoomTypeFilter || selectedRoomTypeFilter === 'all') return;
    const channelKey = selectedChannel === 'all' ? 'combined' : selectedChannel;
    const roomTypeKey = `${channelKey}|${selectedRoomTypeFilter}`;
    setExpandedRoomTypes((prev) => ({ ...prev, [roomTypeKey]: true }));
    if (!loadedRoomIdsRef.current.has(roomIdKey(selectedRoomTypeFilter))) {
      fetchRoomRates(selectedRoomTypeFilter);
    }
  }, [selectedRoomTypeFilter, selectedChannel, dateOffset, fetchRoomRates]);

  const renderInlineRateEditor = ({ compact = false } = {}) => (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        width: compact ? 52 : 58,
        maxWidth: '100%',
        minWidth: 0,
        flexShrink: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 0.5,
        py: 0.25,
        borderRadius: compact ? '8px' : '9px',
        bgcolor: isDark ? alpha('#07131c', 0.95) : alpha('#ffffff', 0.98),
        boxShadow: `inset 0 0 0 1px ${alpha(THEME_COLOR, 0.55)}, 0 0 0 2px ${alpha(THEME_COLOR, 0.12)}`,
        transition: 'box-shadow 140ms ease, background-color 140ms ease',
      }}
    >
      <Typography
        component="span"
        sx={{
          color: THEME_COLOR,
          fontWeight: 800,
          fontSize: compact ? '0.72rem' : '0.78rem',
          lineHeight: 1,
          mr: 0.25,
          flexShrink: 0,
        }}
      >
        ₹
      </Typography>
      <InputBase
        autoFocus
        value={inlineEditValue}
        onChange={(e) => {
          const value = e.target.value;
          if (/^\d*$/.test(value)) {
            setInlineEditValue(value);
          }
        }}
        onBlur={() => {
          commitInlineRateEdit();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelInlineRateEdit();
          }
        }}
        inputProps={{
          inputMode: 'numeric',
          'aria-label': 'Rate amount',
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          '& input': {
            p: 0,
            m: 0,
            border: 0,
            outline: 'none',
            textAlign: 'left',
            fontWeight: 700,
            fontSize: compact ? '0.78rem' : '0.84rem',
            lineHeight: 1.1,
            color: theme.palette.text.primary,
            width: '100%',
            minWidth: 0,
          },
        }}
      />
    </Box>
  );

  return (
    <Box sx={getDashboardPageShellSx(currentTheme)}>
      {/* Header Section */}
      <Box sx={{ mb: isMobileLayout ? 1 : 3 }}>
        <DashboardHeader
          title="Manage Rates"
          breadcrumbs={[
            { label: 'Home', path: '/dashboard/' },
            { label: 'Finance', path: '/finance/' },
            { label: 'Manage Rates' }
          ]}
          navLinks={[
            { url: '/finance/', label: 'Finance Dashboard' },
            { url: '/finance/payments/', label: 'Payment List' },
          ]}
        />
      </Box>

      {/* Calendar Container */}
      <Paper
        elevation={0}
        sx={{
          ...getDashboardPaperSx(currentTheme, 'calendar'),
          minHeight: isMobileLayout ? 0 : '500px',
        }}
      >
              {/* Calendar Header */}
              <Box
                sx={{
                  mb: isMobileLayout ? 0 : 3,
                  ...(isMobileLayout ? inventoryMobileToolbarSx() : {}),
                  display: 'flex',
                  flexDirection: isMobileLayout ? 'column' : 'row',
                  alignItems: isMobileLayout ? 'stretch' : 'center',
                  justifyContent: 'space-between',
                  gap: isMobileLayout ? 1 : 2,
                }}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: isMobileLayout ? 'wrap' : 'nowrap',
                  gap: isMobileLayout ? 0.75 : 2,
                  width: '100%',
                }}>
                <Typography variant={isMobileLayout ? 'subtitle1' : 'h6'} sx={{ fontWeight: 700, flex: isMobileLayout ? '1 1 auto' : undefined }}>
                  {dates[0]?.format('MMMM YYYY') || moment().format('MMMM YYYY')}
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => {
                    const newStartDate = calendarStartDate.clone().subtract(visibleDatesCount, 'days');
                    setIsTransitioning(true);
                    setCalendarStartDate(newStartDate);
                  }}
                  disabled={loading || isTransitioning}
                >
                  <ArrowBackIosIcon fontSize="small" />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={() => {
                    const newStartDate = calendarStartDate.clone().add(visibleDatesCount, 'days');
                    setIsTransitioning(true);
                    setCalendarStartDate(newStartDate);
                  }}
                  disabled={loading || isTransitioning}
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="Start Date"
                    value={calendarStartDate}
                    onChange={(newValue) => {
                      if (newValue) {
                        setIsTransitioning(true);
                        setCalendarStartDate(newValue);
                      }
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { width: isMobileLayout ? '100%' : 150, flex: isMobileLayout ? '1 1 140px' : undefined },
                      },
                  }}
                  disabled={loading || isTransitioning}
                  />
                </LocalizationProvider>
                {hasChannels && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={channelView && selectedChannel === 'all'}
                      onChange={(e) => setChannelView(e.target.checked)}
                      disabled={selectedChannel !== 'all' || loading || isTransitioning}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: THEME_COLOR },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: THEME_COLOR,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                      Channel View
                    </Typography>
                  }
                  sx={{ m: 0, ml: isMobileLayout ? 0 : 1 }}
                />
                )}
                </Box>
                
                {/* Room Type Filter and Action Buttons on Right */}
                <Box sx={{
                  display: 'flex',
                  flexDirection: isMobileLayout ? 'column' : 'row',
                  gap: 1,
                  alignItems: 'stretch',
                  width: isMobileLayout ? '100%' : 'auto',
                }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      width: isMobileLayout ? '100%' : 'auto',
                    }}
                  >
                    <Tooltip title="Refresh rate plans">
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleRefreshRates}
                          disabled={loading || isTransitioning || isRefreshingRates}
                          aria-label="Refresh rate plans"
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            border: `1px solid ${alpha(THEME_COLOR, 0.5)}`,
                            color: isDark ? THEME_COLOR : '#0f766e',
                            backgroundColor: isDark
                              ? alpha(THEME_COLOR, 0.08)
                              : alpha(THEME_COLOR, 0.06),
                            '&:hover': {
                              backgroundColor: alpha(THEME_COLOR, 0.14),
                              borderColor: THEME_COLOR,
                            },
                          }}
                        >
                          {isRefreshingRates ? (
                            <CircularProgress size={18} sx={{ color: THEME_COLOR }} />
                          ) : (
                            <RefreshIcon sx={{ fontSize: 20 }} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  {isMobileLayout ? (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Add />}
                      endIcon={<ArrowDropDownIcon />}
                      onClick={(e) => setActionMenuAnchor(e.currentTarget)}
                      disabled={loading || isTransitioning}
                      sx={{
                        backgroundColor: THEME_COLOR,
                        color: '#000',
                        fontWeight: 600,
                        flex: 1,
                        height: '40px',
                        '&:hover': {
                          backgroundColor: THEME_COLOR,
                          opacity: 0.9,
                        },
                      }}
                    >
                      Action
                    </Button>
                  ) : (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Add />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={(e) => setActionMenuAnchor(e.currentTarget)}
                    disabled={loading || isTransitioning}
                    sx={{
                      backgroundColor: THEME_COLOR,
                      color: '#000',
                      fontWeight: 600,
                      minWidth: 140,
                      height: '40px',
                      '&:hover': {
                        backgroundColor: THEME_COLOR,
                        opacity: 0.9,
                      },
                    }}
                  >
                    Action
                  </Button>
                  )}
                  </Box>
                  <Menu
                    anchorEl={actionMenuAnchor}
                    open={Boolean(actionMenuAnchor)}
                    onClose={() => setActionMenuAnchor(null)}
                    PaperProps={{
                      sx: {
                        ...inventoryMenuPaperSx(isDark, { minWidth: 220, mt: 1 }),
                      },
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        setActionMenuAnchor(null);
                        const pmsPlans = pmsRatePlans.length > 0 ? pmsRatePlans : ratePlans;
                        ensureRatePlanCatalogs();
                        setUpdateDrawerContext({
                          empty: true,
                          roomTypeId:
                            selectedRoomTypeFilter && selectedRoomTypeFilter !== 'all'
                              ? selectedRoomTypeFilter
                              : listings[0]?.id,
                          ratePlanId: pmsPlans[0]?.id ?? null,
                        });
                        setDrawerOpen(true);
                      }}
                      sx={{
                        color: themeColors.text,
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                        },
                      }}
                    >
                      <ListItemIcon>
                        <EditIcon sx={{ color: THEME_COLOR }} />
                      </ListItemIcon>
                      <ListItemText>Update</ListItemText>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setActionMenuAnchor(null);
                        ensureRatePlanCatalogs();
                        setGroupDrawerOpen(true);
                      }}
                      sx={{
                        color: themeColors.text,
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                        },
                      }}
                    >
                      <ListItemIcon>
                        <LayersIcon sx={{ color: THEME_COLOR }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Group Update"
                        secondary="All room types & meal plans"
                        secondaryTypographyProps={{
                          sx: { fontSize: '0.7rem', color: themeColors.subtleText },
                        }}
                      />
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setActionMenuAnchor(null);
                        ensureRatePlanCatalogs();
                        setRestrictionsDrawerOpen(true);
                      }}
                      sx={{
                        color: themeColors.text,
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                        },
                      }}
                    >
                      <ListItemIcon>
                        <BlockIcon sx={{ color: THEME_COLOR }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Restrictions"
                        secondary="CTA / CTD / stay on rate plans"
                        secondaryTypographyProps={{
                          sx: { fontSize: '0.7rem', color: themeColors.subtleText },
                        }}
                      />
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setActionMenuAnchor(null);
                        setStopSellInitialContext(null);
                        ensureRatePlanCatalogs();
                        setStopSellDrawerOpen(true);
                      }}
                      sx={{
                        color: themeColors.text,
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                        },
                      }}
                    >
                      <ListItemIcon>
                        <DoNotDisturbOnIcon sx={{ color: THEME_COLOR }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Stop Sell"
                        secondary="Rate-plan stop sell"
                        secondaryTypographyProps={{
                          sx: { fontSize: '0.7rem', color: themeColors.subtleText },
                        }}
                      />
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setActionMenuAnchor(null);
                        ensureRatePlanCatalogs();
                        setSyncDrawerOpen(true);
                      }}
                      sx={{
                        color: themeColors.text,
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                        },
                      }}
                    >
                      <ListItemIcon>
                        <SyncIcon sx={{ color: THEME_COLOR }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Sync"
                        secondary="Sync current rates to OTAs"
                        secondaryTypographyProps={{
                          sx: { fontSize: '0.7rem', color: themeColors.subtleText },
                        }}
                      />
                    </MenuItem>
                  </Menu>
                  {isMobileLayout ? (
                    <Box sx={inventoryMobileToolbarFilterRowSx()}>
                      <FormControl size="small" sx={{ minWidth: 0, width: '100%' }}>
                        <InputLabel>Room Type</InputLabel>
                        <Select
                          value={selectedRoomTypeFilter || 'all'}
                          label="Room Type"
                          onChange={(e) => setSelectedRoomTypeFilter(e.target.value)}
                          disabled={loading || isTransitioning}
                        >
                          <MenuItem value="all">All Room Types</MenuItem>
                          {listings.map((listing) => (
                            <MenuItem key={listing.id} value={listing.id.toString()}>
                              {listing.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <ChannelSelect
                        value={selectedChannel}
                        onChange={(e) => setSelectedChannel(e.target.value)}
                        channels={channels}
                        label="Channel"
                        allLabel="OTA"
                        includeAll={hasChannels}
                        disabled={loading || isTransitioning}
                        size="small"
                        sx={{ minWidth: 0, width: '100%' }}
                      />
                    </Box>
                  ) : (
                  <FormControl size="small" sx={{ minWidth: 200, width: 'auto' }}>
                    <InputLabel>Room Type</InputLabel>
                    <Select
                      value={selectedRoomTypeFilter || 'all'}
                      label="Room Type"
                      onChange={(e) => setSelectedRoomTypeFilter(e.target.value)}
                      disabled={loading || isTransitioning}
                    >
                      <MenuItem value="all">All Room Types</MenuItem>
                      {listings.map((listing) => (
                        <MenuItem key={listing.id} value={listing.id.toString()}>
                          {listing.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  )}
                </Box>
              </Box>

              {/* Calendar Grid: date section + channel list always visible; only rate boxes use skeleton */}
              <Box 
                sx={{ 
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  width: '100%',
                  position: 'relative',
                  opacity: isTransitioning ? 0.7 : 1,
                  transition: 'opacity 0.2s ease-in-out',
                  minHeight: isMobileLayout ? 0 : '500px',
                  pointerEvents: isTransitioning ? 'none' : 'auto',
                }}
              >
                <Box
                  sx={{
                    opacity: isTransitioning ? 0.75 : 1,
                    transition: 'opacity 0.2s ease-in-out',
                    ...inventoryGridScrollInnerSx(isMobileLayout),
                  }}
                >
              {/* Date Headers — scrolls with rate rows (single horizontal overflow on parent) */}
              <Box
                sx={{
                  ...inventoryGridRowSx(isMobileLayout, dates.length, 250, {
                    mb: 1,
                    zIndex: 10,
                    py: isMobileLayout ? 0 : 1,
                  }),
                }}
              >
                {isMobileLayout ? (
                  <MobileInventoryGridCorner isDark={isDark} label="Day" />
                ) : (
                <Box sx={inventoryChannelCornerCellSx(isDark)}>
                  <ChannelSelect
                    value={selectedChannel}
                    onChange={(e) => setSelectedChannel(e.target.value)}
                    channels={channels}
                    label="Channel"
                    includeAll={hasChannels}
                    disabled={loading || isTransitioning}
                    size="small"
                  />
                </Box>
                )}
                {dates.map((date) => {
                  const isToday = date.isSame(moment(), 'day');
                  const isWeekend = isWeekendDate(date);
                  return (
                  <Box
                    key={date.format('YYYY-MM-DD')}
                    sx={inventoryDateHeaderCellSx(isMobileLayout, isDark, isToday, isWeekend)}
                  >
                    <Typography variant="caption" sx={inventoryDateHeaderLabelSx(isMobileLayout)} display="block">
                      {date.format(isMobileLayout ? 'dd' : 'ddd')}
                    </Typography>
                    <Typography variant="body2" sx={inventoryDateHeaderDaySx(isMobileLayout, isToday)}>
                      {date.format('D')}
                    </Typography>
                  </Box>
                );
                })}
              </Box>

              {/* Only daily rate boxes: skeleton on cold load; keep stale rows during refetch */}
              {isRateGridLoading ? (
                <InventoryGridSkeleton
                  isMobile={isMobileLayout}
                  dates={dates}
                  isDark={isDark}
                  theme={theme}
                  desktopLabelWidth={250}
                  mode={
                    selectedChannel === 'all'
                      ? (channelView ? 'channels' : 'combined')
                      : 'listings'
                  }
                  rowCount={
                    selectedChannel === 'all'
                      ? (channelView ? Math.max(4, (channels?.length || 0) + 1) : 2)
                      : Math.max(3, filteredListings.length || listings.length || 4)
                  }
                  withExpandedRooms={selectedChannel === 'all' && !channelView && selectedRoomTypeFilter === 'all'}
                  roomTypeCount={Math.min(4, listings.length || 3)}
                />
              ) : (
              selectedChannel === 'all' ? (
                // Display channels as rows when "All Channels" is selected
                displayChannels.map((channel) => {
                  const isCombined = Boolean(channel.isCombined);
                  const firstOtaId =
                    Array.isArray(channels) && channels[0] != null
                      ? String(channels[0].id)
                      : null;
                  // Combined OTA row reads/edits via first OTA; commitInlineRateEdit still writes all OTAs once
                  const channelIdKey = channel.id === 'pms'
                    ? 'pms'
                    : isCombined
                      ? 'combined'
                      : String(channel.id);
                  const editChannelId = isCombined
                    ? (firstOtaId || channel.id)
                    : channel.id;
                  const channelRates = isCombined
                    ? mergeOtaChannelListings(allChannelsRates, channels)
                    : allChannelsRates[channelIdKey];
                  const hasOtaListings = Object.keys(allChannelsRates || {}).some((key) => key !== 'pms');
                  const channelDataLoading = isCombined
                    ? !hasOtaListings
                    : channelRates === undefined;
                  const isExpanded = Boolean(expandedChannels[channelIdKey]);
                  const canExpand = selectedRoomTypeFilter === 'all';
                  const roomTypesForChannel = Array.isArray(channelRates) && channelRates.length > 0
                    ? channelRates
                    : listings;
                  // Prefer first listing order as the canonical "first room type"
                  const firstRoomType =
                    (listings[0]
                      && roomTypesForChannel.find(
                        (r) => r.id.toString() === listings[0].id.toString()
                      ))
                    || roomTypesForChannel[0]
                    || null;
                  const summaryRoomTypeId = canExpand
                    ? (firstRoomType?.id != null ? String(firstRoomType.id) : null)
                    : selectedRoomTypeFilter;
                  const {
                    rates,
                    primaryRatePlanId,
                  } = getPrimaryRateBundle(channelRates, summaryRoomTypeId);
                  
                  // Skeleton row when this channel's data is still loading (no red, no empty state)
                  if (channelDataLoading) {
                    return (
                      <MobileInventorySkeletonRow
                        key={channel.id}
                        isMobile={isMobileLayout}
                        dates={dates}
                        isDark={isDark}
                        theme={theme}
                        desktopLabelWidth={250}
                      />
                    );
                  }
                  
                  return (
                    <React.Fragment key={channel.id}>
                    <Box
                      sx={inventoryGridRowSx(isMobileLayout, dates.length, 250, {
                        mb: isExpanded && canExpand ? 0.5 : 1,
                        width: '100%',
                      })}
                    >
                      <Box
                        sx={{
                          ...inventoryStickyLabelCellSx(isMobileLayout, isDark),
                          cursor: canExpand ? 'pointer' : 'default',
                          userSelect: 'none',
                          transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                          borderBottom: isExpanded && canExpand
                            ? `2px solid ${THEME_COLOR}`
                            : undefined,
                          backgroundColor: isExpanded && canExpand
                            ? (isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)')
                            : undefined,
                          '&:hover': canExpand
                            ? {
                                backgroundColor: isDark
                                  ? 'rgba(76, 225, 182, 0.16)'
                                  : 'rgba(76, 225, 182, 0.12)',
                              }
                            : undefined,
                        }}
                        onClick={canExpand ? () => toggleChannelExpand(channelIdKey) : undefined}
                        role={canExpand ? 'button' : undefined}
                        aria-expanded={canExpand ? isExpanded : undefined}
                        title={canExpand ? (isExpanded ? 'Hide room types' : 'Show room types') : channel.name}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0, flexDirection: isMobileLayout ? 'column' : 'row' }}>
                        {canExpand && !isMobileLayout && (
                          <ExpandMoreIcon
                            sx={{
                              fontSize: 18,
                              color: THEME_COLOR,
                              flexShrink: 0,
                              transition: 'transform 0.25s ease',
                              transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                            }}
                          />
                        )}
                        {isCombined ? (
                          <Typography
                            variant="body2"
                            noWrap
                            title="All Channels"
                            sx={{
                              fontWeight: isExpanded ? 700 : 600,
                              color: theme.palette.text.primary,
                              fontSize: { xs: '0.7rem', sm: '0.875rem' },
                              minWidth: 0,
                              textAlign: isMobileLayout ? 'center' : 'left',
                            }}
                          >
                            {isMobileLayout ? 'OTA' : 'All Channels'}
                          </Typography>
                        ) : (
                          <>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: isMobileLayout ? 22 : 32,
                            height: isMobileLayout ? 22 : 32,
                            flexShrink: 0,
                          }}
                        >
                          <ChannelImage channel={channel} size={isMobileLayout ? 22 : 32} />
                        </Box>
                        {!isMobileLayout && (
                        <Typography
                          variant="body2"
                          title={channel.name}
                          sx={{
                            fontWeight: isExpanded ? 700 : 500,
                            color: theme.palette.text.primary,
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          }}
                        >
                          {shortChannelName(channel.name)}
                        </Typography>
                        )}
                          </>
                        )}
                        </Box>
                        {canExpand && (
                          <Chip
                            label={roomTypesForChannel.length}
                            size="small"
                            sx={{
                              height: isMobileLayout ? 14 : 20,
                              fontSize: isMobileLayout ? '0.5rem' : '0.65rem',
                              fontWeight: 700,
                              backgroundColor: THEME_COLOR,
                              color: '#000',
                              '& .MuiChip-label': { px: isMobileLayout ? 0.35 : 0.75 },
                            }}
                          />
                        )}
                      </Box>
                      
                      {dates.map((date) => {
                        const dateStr = date.format('YYYY-MM-DD');
                        // All Room Types: show first room type's rate (edit on expanded room-type rows only)
                        const rate = rates[dateStr];
                        const editRoomTypeId = summaryRoomTypeId;
                        const editRatePlanId = primaryRatePlanId;
                        const isSelected = selectedDates.some((sd) => sd.start === dateStr);
                        const sourcePending = isRatesPending(isCombined ? 'combined' : channelIdKey);
                        // Only show empty/red after this channel's rates have arrived
                        const isEmpty = !rate && !sourcePending;
                        const isBlocked = rate && rate.available === false;
                        const isStopSell = !!(rate && rate.stop_sell);
                        const hasRate = rate && rate.available !== false && !sourcePending;
                        const showAlert = isEmpty || isBlocked || isStopSell;
                        const cellLoading = sourcePending;
                        // Use a distinct summary key when All Room Types so it never collides with
                        // the first nested room-type row (Budget Room) edit/saving state.
                        const realCellKey = makeRateCellKey(
                          editChannelId,
                          dateStr,
                          editRoomTypeId,
                          editRatePlanId
                        );
                        const cellKey = canExpand
                          ? `summary|${realCellKey}`
                          : realCellKey;
                        const isEditing = !canExpand && inlineEdit?.key === cellKey;
                        const isCellSaving = savingCellKeys.has(realCellKey);
                        const summaryReadOnly = canExpand;
                        
                        return (
                          <StatusCellTooltip
                            key={dateStr}
                            title={
                              getRateCellTooltip(rate)
                              || (summaryReadOnly
                                ? 'Expand channel and edit rates on the room type row'
                                : '')
                            }
                          >
                          <Paper
                            elevation={isSelected ? 3 : 0}
                            sx={inventoryDataCellSx(isMobileLayout, {
                              p: { xs: 0.5, sm: 1 },
                              textAlign: 'center',
                              cursor: summaryReadOnly
                                ? 'default'
                                : isCellSaving
                                ? 'wait'
                                : 'pointer',
                              position: 'relative',
                              boxSizing: 'border-box',
                              width: '100%',
                              minWidth: 0,
                              maxWidth: '100%',
                              overflow: 'hidden',
                              border: isEditing
                                ? `1px solid ${THEME_COLOR}`
                                : isSelected
                                ? `2px solid ${theme.palette.primary.main}`
                                : cellLoading || isCellSaving
                                ? `1px solid ${theme.palette.divider}`
                                : showAlert
                                ? `1px solid #c1807b`
                                : `1px solid #a0c49e`,
                              borderRadius: showAlert ? 2 : 1,
                              ...inventoryRateCellFillSx(isDark, isSelected || isEditing, isWeekendDate(date)),
                              minHeight: { xs: '50px', sm: '60px' },
                              height: { xs: '50px', sm: '60px' },
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              transition: 'border-color 160ms ease, background-color 160ms ease',
                            })}
                            onClick={() => {
                              if (summaryReadOnly || isCellSaving || isEditing || !editRoomTypeId) return;
                              beginInlineRateEdit({
                                channelId: editChannelId,
                                date,
                                roomTypeId: editRoomTypeId,
                                ratePlanId: editRatePlanId,
                                currentRate: rate,
                              });
                            }}
                          >
                            {hasRate && !cellLoading && !isCellSaving && !isStopSell && !isBlocked && !isEditing && (
                              <Box sx={inventoryStatusDotSx(isMobileLayout, theme.palette.success.main)} />
                            )}
                            
                            {!cellLoading && !isCellSaving && showAlert && !isEditing && (
                              <Box sx={inventoryStatusDotSx(isMobileLayout, theme.palette.error.main)} />
                            )}
                            
                            {cellLoading || isCellSaving ? (
                              <Skeleton variant="rounded" width="72%" height={22} animation="wave" sx={{ mx: 'auto' }} />
                            ) : isEditing ? (
                              renderInlineRateEditor()
                            ) : rate ? (
                              <>
                                <Typography variant="body2" sx={rateAmountTextSx}>
                                  {formatCurrency(rate.base_rate)}
                                </Typography>
                                {rate.available === false && (
                                  <Chip
                                    label="Blocked"
                                    size="small"
                                    color="error"
                                    sx={{ mt: 0.5, height: 16, fontSize: '0.65rem' }}
                                  />
                                )}
                              </>
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  height: '100%',
                                  width: '100%',
                                }}
                              >
                                <Typography variant="body2" color="text.secondary">
                                  —
                                </Typography>
                              </Box>
                            )}
                          </Paper>
                          </StatusCellTooltip>
                        );
                      })}
                    </Box>

                    {canExpand && (
                      <Collapse in={isExpanded} timeout={280} unmountOnExit>
                        <Box
                          sx={{
                            mb: 1.25,
                            ml: { xs: 0, sm: 0.5 },
                            pl: { xs: 0, sm: 0.5 },
                            borderLeft: { xs: 'none', sm: `2px solid ${alpha(THEME_COLOR, 0.4)}` },
                            '@keyframes rateRoomReveal': {
                              from: { opacity: 0, transform: 'translateY(-6px)' },
                              to: { opacity: 1, transform: 'translateY(0)' },
                            },
                          }}
                        >
                          {roomTypesForChannel.map((roomType, roomTypeIndex) => {
                            const bundle = getPrimaryRateBundle(channelRates, roomType.id);
                            const roomRates = bundle.rates || {};
                            const roomPlanId = bundle.primaryRatePlanId;
                            const ratePlanRows = getRatePlanRows(channelRates, roomType.id);
                            const planCount = ratePlanRows.length;
                            const roomTypeKey = `${channelIdKey}|${roomType.id}`;
                            const isRoomTypeExpanded = Boolean(expandedRoomTypes[roomTypeKey]);
                            const roomReady = loadedRoomIds.has(roomIdKey(roomType.id)) || planCount > 0;
                            const sourcePending = isRatesPending(isCombined ? 'combined' : channelIdKey);
                            const roomLoading = sourcePending || (loadingRoomIds.has(roomIdKey(roomType.id)) && planCount === 0);
                            const canExpandPlans = planCount > 0 || roomLoading || !roomReady;

                            return (
                              <React.Fragment key={`${channel.id}-${roomType.id}`}>
                              <Box
                                sx={{
                                  ...inventoryGridRowSx(isMobileLayout, dates.length, 250, {
                                    mb: isRoomTypeExpanded
                                      ? 0.5
                                      : roomTypeIndex === roomTypesForChannel.length - 1 ? 0 : 0.5,
                                    width: '100%',
                                  }),
                                  animation: 'rateRoomReveal 0.28s ease both',
                                  animationDelay: `${Math.min(roomTypeIndex, 8) * 35}ms`,
                                }}
                              >
                                <Box
                                  sx={{
                                    p: { xs: 0.35, sm: 1 },
                                    pl: { xs: 0.5, sm: 2 },
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    borderRight: `1px solid ${theme.palette.divider}`,
                                    backgroundColor: isRoomTypeExpanded
                                      ? (isDark ? alpha(THEME_COLOR, 0.1) : alpha(THEME_COLOR, 0.07))
                                      : (isDark ? themeColors.surface : '#ffffff'),
                                    color: theme.palette.text.primary,
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 1,
                                    borderLeft: isMobileLayout ? 'none' : `3px solid ${THEME_COLOR}`,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    cursor: canExpandPlans ? 'pointer' : 'default',
                                    userSelect: 'none',
                                    transition: 'background-color 0.2s ease',
                                    '&:hover': canExpandPlans
                                      ? {
                                          backgroundColor: isDark
                                            ? alpha(THEME_COLOR, 0.16)
                                            : alpha(THEME_COLOR, 0.12),
                                        }
                                      : undefined,
                                  }}
                                  onClick={() => handleRoomTypeActivate(roomTypeKey, roomType.id)}
                                  role={canExpandPlans ? 'button' : undefined}
                                  aria-expanded={canExpandPlans ? isRoomTypeExpanded : undefined}
                                  title={
                                    canExpandPlans
                                      ? (isRoomTypeExpanded ? 'Hide rate plans' : 'Show rate plans')
                                      : (roomType.name || `Room Type ${roomType.id}`)
                                  }
                                >
                                  {canExpandPlans && !isMobileLayout && (
                                    <ExpandMoreIcon
                                      sx={{
                                        fontSize: 16,
                                        color: THEME_COLOR,
                                        flexShrink: 0,
                                        transition: 'transform 0.25s ease',
                                        transform: isRoomTypeExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                                      }}
                                    />
                                  )}
                                  <InventoryRoomTypeLabel
                                    roomType={roomType}
                                    isMobile={isMobileLayout}
                                    isDark={isDark}
                                    fontWeight={isRoomTypeExpanded ? 700 : 600}
                                    endAdornment={(
                                      <Chip
                                        label={planCount || (roomLoading ? '…' : 0)}
                                        size="small"
                                        sx={{
                                          height: isMobileLayout ? 14 : 18,
                                          fontSize: isMobileLayout ? '0.5rem' : '0.6rem',
                                          fontWeight: 700,
                                          backgroundColor: THEME_COLOR,
                                          color: '#000',
                                          flexShrink: 0,
                                          '& .MuiChip-label': { px: isMobileLayout ? 0.35 : 0.5 },
                                        }}
                                      />
                                    )}
                                  />
                                </Box>

                                {dates.map((date) => {
                                  const dateStr = date.format('YYYY-MM-DD');
                                  const rate = roomRates[dateStr];
                                  const isEmpty = !rate && !sourcePending && !roomLoading;
                                  const isBlocked = rate && rate.available === false;
                                  const isStopSell = !!(rate && rate.stop_sell);
                                  const hasRate = rate && rate.available !== false && !sourcePending;
                                  const showAlert = isEmpty || isBlocked || isStopSell;
                                  const cellLoading = sourcePending || roomLoading;
                                  const cellKey = makeRateCellKey(
                                    editChannelId,
                                    dateStr,
                                    roomType.id,
                                    roomPlanId
                                  );
                                  const isEditing = inlineEdit?.key === cellKey;
                                  const isCellSaving = savingCellKeys.has(cellKey);

                                  return (
                                    <StatusCellTooltip
                                      key={dateStr}
                                      title={getRateCellTooltip(rate)}
                                    >
                                    <Paper
                                      elevation={0}
                                      sx={{
                                        p: { xs: 0.25, sm: 0.5 },
                                        textAlign: 'center',
                                        cursor: isCellSaving ? 'wait' : 'pointer',
                                        minHeight: { xs: '34px', sm: '40px' },
                                        height: { xs: '34px', sm: '40px' },
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        position: 'relative',
                                        border: isEditing
                                          ? `1px solid ${THEME_COLOR}`
                                          : cellLoading || isCellSaving
                                          ? `1px solid ${theme.palette.divider}`
                                          : showAlert
                                          ? `1px solid #c1807b`
                                          : `1px solid #a0c49e`,
                                        borderRadius: showAlert ? 2 : 1.5,
                                        ...inventoryRateCellFillSx(isDark, isEditing, isWeekendDate(date)),
                                        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                                        '&:hover': {
                                          ...inventoryRateCellFillSx(isDark, false, isWeekendDate(date))['&:hover'],
                                          transform: 'scale(1.03)',
                                          zIndex: 10,
                                          boxShadow: `0 2px 10px ${alpha(THEME_COLOR, 0.22)}`,
                                        },
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (isCellSaving) return;
                                        if (isEditing) return;
                                        beginInlineRateEdit({
                                          channelId: editChannelId,
                                          date,
                                          roomTypeId: roomType.id.toString(),
                                          ratePlanId: roomPlanId,
                                          currentRate: rate,
                                        });
                                      }}
                                    >
                                      {cellLoading || isCellSaving ? (
                                        <Skeleton variant="rounded" width="70%" height={16} animation="wave" />
                                      ) : isEditing ? (
                                        renderInlineRateEditor({ compact: true })
                                      ) : rate ? (
                                        <>
                                          <Typography
                                            sx={{
                                              ...rateAmountTextSx,
                                              color: isBlocked
                                                ? theme.palette.error.main
                                                : theme.palette.text.primary,
                                            }}
                                          >
                                            {formatCurrency(rate.base_rate)}
                                          </Typography>
                                          {(isBlocked || isStopSell || isEmpty || hasRate) ? (
                                            <Box
                                              sx={inventoryStatusDotSx(
                                                isMobileLayout,
                                                isBlocked || isStopSell || isEmpty
                                                  ? theme.palette.error.main
                                                  : theme.palette.success.main,
                                              )}
                                            />
                                          ) : null}
                                        </>
                                      ) : (
                                        <Typography variant="caption" color="text.secondary">
                                          —
                                        </Typography>
                                      )}
                                    </Paper>
                                    </StatusCellTooltip>
                                  );
                                })}
                              </Box>

                              {canExpandPlans && (
                                <Collapse in={isRoomTypeExpanded} timeout={260} unmountOnExit>
                                  <Box
                                    sx={{
                                      mb: 0.75,
                                      ml: { xs: 0, sm: 1.5 },
                                      pl: { xs: 0, sm: 0.5 },
                                      borderLeft: {
                                        xs: 'none',
                                        sm: `2px dashed ${alpha(THEME_COLOR, 0.35)}`,
                                      },
                                    }}
                                  >
                                    {roomLoading ? (
                                      <Box sx={{ py: 1, pl: { xs: 2, sm: 3.5 }, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CircularProgress size={14} sx={{ color: THEME_COLOR }} />
                                        <Typography variant="caption" color="text.secondary">
                                          Loading rate plans…
                                        </Typography>
                                      </Box>
                                    ) : ratePlanRows.length === 0 ? (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', py: 1, pl: { xs: 2, sm: 3.5 } }}
                                      >
                                        No rate plans for this room
                                      </Typography>
                                    ) : ratePlanRows.map((plan, planIndex) => (
                                      <Box
                                        key={`${channel.id}-${roomType.id}-${plan.ratePlanId}`}
                                        sx={{
                                          ...inventoryGridRowSx(isMobileLayout, dates.length, 250, {
                                            mb: planIndex === ratePlanRows.length - 1 ? 0 : 0.4,
                                            width: '100%',
                                          }),
                                          animation: 'rateRoomReveal 0.26s ease both',
                                          animationDelay: `${Math.min(planIndex, 8) * 30}ms`,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            p: { xs: 0.35, sm: 0.5 },
                                            pl: { xs: 2, sm: 3.5 },
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            borderRight: `1px solid ${theme.palette.divider}`,
                                            backgroundColor: isDark ? themeColors.surface : '#ffffff',
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 1,
                                            minWidth: 0,
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              width: 6,
                                              height: 6,
                                              borderRadius: '50%',
                                              backgroundColor: alpha(THEME_COLOR, 0.8),
                                              flexShrink: 0,
                                            }}
                                          />
                                          <Typography
                                            variant="caption"
                                            noWrap
                                            title={plan.code ? `${plan.name} (${plan.code})` : plan.name}
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: { xs: '0.58rem', sm: '0.72rem' },
                                              color: theme.palette.text.secondary,
                                              flex: 1,
                                              minWidth: 0,
                                              letterSpacing: isMobileLayout ? '0.02em' : 0,
                                            }}
                                          >
                                            {isMobileLayout
                                              ? (plan.code || plan.name)
                                              : plan.name}
                                          </Typography>
                                        </Box>

                                        {dates.map((date) => {
                                          const dateStr = date.format('YYYY-MM-DD');
                                          const planRate = plan.rates?.[dateStr];
                                          const planEmpty = !planRate && !sourcePending;
                                          const planBlocked = planRate && planRate.available === false;
                                          const planStopSell = !!(planRate && planRate.stop_sell);
                                          const planAlert = planEmpty || planBlocked || planStopSell;
                                          const planCellLoading = sourcePending;
                                          const planRealKey = makeRateCellKey(
                                            editChannelId,
                                            dateStr,
                                            roomType.id,
                                            plan.ratePlanId
                                          );
                                          // Prefix keeps this distinct from the room-type summary row
                                          const planEditKey = `plan|${planRealKey}`;
                                          const planEditing = inlineEdit?.key === planEditKey;
                                          const planSaving = savingCellKeys.has(planRealKey);

                                          return (
                                            <StatusCellTooltip
                                              key={dateStr}
                                              title={getRateCellTooltip(planRate)}
                                            >
                                            <Paper
                                              elevation={0}
                                              sx={{
                                                p: { xs: 0.2, sm: 0.35 },
                                                textAlign: 'center',
                                                cursor: planSaving ? 'wait' : 'pointer',
                                                minHeight: { xs: '28px', sm: '32px' },
                                                height: { xs: '28px', sm: '32px' },
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                position: 'relative',
                                                border: planEditing
                                                  ? `1px solid ${THEME_COLOR}`
                                                  : planCellLoading || planSaving
                                                  ? `1px solid ${theme.palette.divider}`
                                                  : planAlert
                                                  ? `1px solid ${alpha('#c1807b', 0.7)}`
                                                  : `1px solid ${alpha('#a0c49e', 0.7)}`,
                                                borderRadius: 1.25,
                                                ...inventoryRateCellFillSx(isDark, planEditing, isWeekendDate(date)),
                                                transition: 'transform 0.16s ease, box-shadow 0.16s ease',
                                                '&:hover': {
                                                  ...inventoryRateCellFillSx(isDark, false, isWeekendDate(date))['&:hover'],
                                                  transform: 'scale(1.04)',
                                                  zIndex: 10,
                                                  boxShadow: `0 2px 8px ${alpha(THEME_COLOR, 0.2)}`,
                                                },
                                              }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (planSaving || planEditing) return;
                                                beginInlineRateEdit({
                                                  channelId: editChannelId,
                                                  date,
                                                  roomTypeId: roomType.id.toString(),
                                                  ratePlanId: plan.ratePlanId,
                                                  currentRate: planRate,
                                                  keyPrefix: 'plan|',
                                                });
                                              }}
                                            >
                                              {planCellLoading || planSaving ? (
                                                <Skeleton variant="rounded" width="66%" height={12} animation="wave" />
                                              ) : planEditing ? (
                                                renderInlineRateEditor({ compact: true })
                                              ) : planRate ? (
                                                <Typography
                                                  sx={{
                                                    ...rateAmountTextSx,
                                                    fontSize: isMobileLayout ? '0.66rem' : '0.72rem',
                                                    color: planBlocked
                                                      ? theme.palette.error.main
                                                      : theme.palette.text.primary,
                                                  }}
                                                >
                                                  {formatCurrency(planRate.base_rate)}
                                                </Typography>
                                              ) : (
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                  sx={{ fontSize: '0.62rem' }}
                                                >
                                                  —
                                                </Typography>
                                              )}
                                            </Paper>
                                            </StatusCellTooltip>
                                          );
                                        })}
                                      </Box>
                                    ))}
                                  </Box>
                                </Collapse>
                              )}
                              </React.Fragment>
                            );
                          })}
                        </Box>
                      </Collapse>
                    )}
                    </React.Fragment>
                  );
                })
              ) : (
                // Display room types with all rate plans as rows when specific channel is selected
                filteredListings.map((listing) => {
                  const roomTypeKey = `${selectedChannel}|${listing.id}`;
                  const isRoomTypeExpanded = Boolean(expandedRoomTypes[roomTypeKey]);
                  const ratePlansArray = sortRatePlans(Object.values(listing.rate_plans || {}));
                  const sourcePending = isRatesPending(selectedChannel);
                  const roomReady = loadedRoomIds.has(roomIdKey(listing.id)) || ratePlansArray.length > 0;
                  const roomLoading = sourcePending || (loadingRoomIds.has(roomIdKey(listing.id)) && ratePlansArray.length === 0);
                  const primaryRates = ratePlansArray[0]?.rates || {};
                  const showRoomTypeChip = selectedRoomTypeFilter === 'all';
                  return (
                    <React.Fragment key={listing.id}>
                      <Box
                        sx={inventoryGridRowSx(isMobileLayout, dates.length, 250, { mb: 0.5, width: '100%' })}
                      >
                        <Box
                          onClick={() => handleRoomTypeActivate(roomTypeKey, listing.id)}
                          sx={{
                            p: { xs: 0.5, sm: 1 },
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            borderRight: `1px solid ${theme.palette.divider}`,
                            backgroundColor: isRoomTypeExpanded
                              ? (isDark ? alpha(THEME_COLOR, 0.1) : alpha(THEME_COLOR, 0.07))
                              : (isDark ? themeColors.surface : '#ffffff'),
                            position: 'sticky',
                            left: 0,
                            zIndex: 1,
                            minWidth: 0,
                            cursor: 'pointer',
                            userSelect: 'none',
                          }}
                        >
                          {!isMobileLayout && (
                            <ExpandMoreIcon
                              sx={{
                                fontSize: 16,
                                color: THEME_COLOR,
                                flexShrink: 0,
                                transform: isRoomTypeExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                                transition: 'transform 0.25s ease',
                              }}
                            />
                          )}
                          <InventoryRoomTypeLabel
                            roomType={listing}
                            isMobile={isMobileLayout}
                            isDark={isDark}
                            fontWeight={isRoomTypeExpanded ? 700 : 600}
                            endAdornment={(
                              <Chip
                                label={ratePlansArray.length || (roomLoading ? '…' : 0)}
                                size="small"
                                sx={{
                                  height: isMobileLayout ? 14 : 18,
                                  fontSize: isMobileLayout ? '0.5rem' : '0.6rem',
                                  fontWeight: 700,
                                  backgroundColor: THEME_COLOR,
                                  color: '#000',
                                  '& .MuiChip-label': { px: 0.5 },
                                }}
                              />
                            )}
                          />
                        </Box>
                        {dates.map((date) => {
                          const dateStr = date.format('YYYY-MM-DD');
                          const rate = primaryRates[dateStr];
                          const isEmpty = !rate && !sourcePending && !roomLoading;
                          const isStopSell = !!(rate && (rate.stop_sell || rate.stopSell));
                          const hasRate = rate && rate.available !== false && !sourcePending;
                          return (
                          <Paper
                            key={dateStr}
                            elevation={0}
                            onClick={() => handleRoomTypeActivate(roomTypeKey, listing.id)}
                            sx={{
                              ...inventoryDataCellSx(isMobileLayout, {
                                p: { xs: 0.5, sm: 1 },
                                textAlign: 'center',
                                cursor: 'pointer',
                                minHeight: { xs: '50px', sm: '60px' },
                                height: { xs: '50px', sm: '60px' },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: isEmpty || isStopSell
                                  ? `1px solid #c1807b`
                                  : hasRate
                                  ? `1px solid #a0c49e`
                                  : `1px solid ${theme.palette.divider}`,
                                backgroundColor: isWeekendDate(date)
                                  ? inventoryWeekendWash(isDark)
                                  : undefined,
                              }),
                            }}
                          >
                            {roomLoading || sourcePending ? (
                              <Skeleton variant="rounded" width="72%" height={22} animation="wave" />
                            ) : (
                              <Typography variant="body2" sx={rateAmountTextSx}>
                                {hasRate ? formatCurrency(rate.base_rate) : '—'}
                              </Typography>
                            )}
                          </Paper>
                          );
                        })}
                      </Box>
                      {isRoomTypeExpanded && roomLoading && (
                        <Box sx={{ py: 1, pl: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={14} sx={{ color: THEME_COLOR }} />
                          <Typography variant="caption" color="text.secondary">
                            Loading rate plans…
                          </Typography>
                        </Box>
                      )}
                      {isRoomTypeExpanded && !roomLoading && ratePlansArray.length === 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', py: 1, pl: 2 }}>
                          No rate plans for this room
                        </Typography>
                      )}
                      {isRoomTypeExpanded && !roomLoading && ratePlansArray.map((ratePlanData, planIndex) => {
                    const rates = ratePlanData.rates || {};
                    
                    // Define sub-rows for each rate plan
                    const subRows = [
                      { key: 'x1', label: 'x1', icon: '👤', color: theme.palette.text.secondary, getValue: (rate) => rate?.single_occupancy_rate ?? rate?.base_rate ?? null },
                      { key: 'x2', label: 'x2', icon: '👤', color: theme.palette.text.secondary, getValue: (rate) => rate?.base_rate ?? null },
                      { key: 'adult', label: '+ 1 Adult', icon: '👤', color: '#d4a574', getValue: (rate) => rate?.extra_adult_rate ?? null },
                      { key: 'child', label: '+ 1 Child', icon: '👤', color: '#d4a574', getValue: (rate) => rate?.extra_child_rate ?? null },
                    ];
                    
                    return (
                      <React.Fragment key={`${listing.id}-${ratePlanData.rate_plan_id}`}>
                        {/* Rate Plan Header Row */}
                        <Box
                  sx={inventoryGridRowSx(isMobileLayout, dates.length, 250, { mb: 0.5, width: '100%' })}
                >
                  <Box
                    sx={{
                      p: { xs: 0.5, sm: 1 },
                      display: 'flex',
                      alignItems: 'center',
                              gap: 1,
                              flexWrap: 'wrap',
                      borderRight: `1px solid ${theme.palette.divider}`,
                              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.08)',
                              color: theme.palette.primary.main,
                              fontWeight: 500,
                              position: 'sticky',
                              left: 0,
                              zIndex: 1,
                              minWidth: 0,
                    }}
                  >
                            {showRoomTypeChip && (
                              <Chip
                                label={listing.name}
                                size="small"
                                title={listing.name}
                                sx={{
                                  height: 18,
                                  maxWidth: 120,
                                  fontSize: '0.6rem',
                                  fontWeight: 700,
                                  backgroundColor: THEME_COLOR,
                                  color: '#000',
                                  flexShrink: 0,
                                  '& .MuiChip-label': {
                                    px: 0.5,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  },
                                }}
                              />
                            )}
                            <Typography
                              variant="body2"
                              noWrap
                              title={
                                ratePlanData.rate_plan_code
                                  ? `${ratePlanData.rate_plan_name || ''} (${ratePlanData.rate_plan_code})`
                                  : (ratePlanData.rate_plan_name || ratePlanData.rate_plan_code)
                              }
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.primary.main,
                                fontSize: { xs: '0.65rem', sm: '0.875rem' },
                                flex: 1,
                                minWidth: 0,
                                letterSpacing: isMobileLayout ? '0.02em' : 0,
                              }}
                            >
                              {isMobileLayout
                                ? (ratePlanData.rate_plan_code || ratePlanData.rate_plan_name)
                                : (ratePlanData.rate_plan_name || ratePlanData.rate_plan_code)}
                    </Typography>
                  </Box>
                  
                  {dates.map((date) => {
                    const dateStr = date.format('YYYY-MM-DD');
                            const rate = rates[dateStr];
                            const isLoading = sourcePending;
                            const isStopSell = !!(rate && (rate.stop_sell || rate.stopSell));
                            const hasRate = rate && rate.available !== false && !sourcePending;
                            const channelForEdit = selectedChannel === 'all' ? 'pms' : selectedChannel;
                            const cellKey = makeRateCellKey(channelForEdit, dateStr, listing.id, ratePlanData.rate_plan_id);
                            const isEditing = inlineEdit?.key === cellKey;
                            const isCellSaving = savingCellKeys.has(cellKey);
                    
                    return (
                      <StatusCellTooltip
                        key={dateStr}
                        title={getRateCellTooltip(rate)}
                      >
                      <Paper
                                onClick={() => {
                                  if (isCellSaving || isEditing) return;
                                  beginInlineRateEdit({
                                    channelId: channelForEdit,
                                    date,
                                    roomTypeId: listing.id,
                                    ratePlanId: ratePlanData.rate_plan_id,
                                    currentRate: rate,
                                  });
                                }}
                        sx={{
                          p: { xs: 0.5, sm: 1 },
                          textAlign: 'center',
                          cursor: isCellSaving ? 'wait' : 'pointer',
                                  position: 'relative',
                                  boxSizing: 'border-box',
                                  width: '100%',
                                  minWidth: 0,
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                  border: isEditing
                                    ? `1px solid ${THEME_COLOR}`
                                    : isLoading || isCellSaving
                                    ? `1px solid ${theme.palette.divider}`
                                    : isStopSell
                                    ? `1px solid #c1807b`
                                    : hasRate
                                    ? `1px solid #a0c49e`
                                    : `1px solid #e0e0e0`,
                                  borderRadius: isStopSell ? 2 : 1,
                                  backgroundColor: isWeekendDate(date)
                                    ? inventoryWeekendWash(isDark)
                                    : theme.palette.background.paper,
                                  minHeight: { xs: isStopSell ? '48px' : '35px', sm: isStopSell ? '52px' : '40px' },
                          height: { xs: isStopSell ? '48px' : '35px', sm: isStopSell ? '52px' : '40px' },
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 0.25,
                                  transition: 'border-color 160ms ease, background-color 160ms ease',
                                  '&:hover': {
                                    backgroundColor: theme.palette.action.hover,
                                  },
                                }}
                      >
                                {!isLoading && !isCellSaving && isStopSell && !isEditing && (
                                  <Box sx={inventoryStatusDotSx(isMobileLayout, theme.palette.error.main)} />
                                )}
                                {isLoading || isCellSaving ? (
                                  <Skeleton variant="rounded" width="70%" height={18} animation="wave" />
                                ) : isEditing ? (
                                  renderInlineRateEditor({ compact: true })
                                ) : rate && hasRate ? (
                                  <Typography variant="body2" sx={rateAmountTextSx}>
                                    {formatCurrency(rate.base_rate)}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">—</Typography>
                                )}
                                {!isLoading && !isCellSaving && isStopSell && !isEditing && (
                                  <Chip
                                    label="Stop"
                                    size="small"
                                    color="error"
                                    sx={{ height: 14, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }}
                                  />
                                )}
                              </Paper>
                              </StatusCellTooltip>
                            );
                          })}
                        </Box>
                        
                        {/* Sub-rows for rate details */}
                        {subRows.map((subRow, subIndex) => (
                            <Box
                            key={subRow.key}
                              sx={inventoryGridRowSx(isMobileLayout, dates.length, 250, {
                                mb: planIndex < ratePlansArray.length - 1 || subIndex < subRows.length - 1 ? 0.5 : 1,
                                width: '100%',
                              })}
                          >
                            <Box
                              sx={{
                                p: { xs: 0.5, sm: 1 },
                                pl: { xs: 1.5, sm: 3 },
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                borderRight: `1px solid ${theme.palette.divider}`,
                                backgroundColor: theme.palette.background.paper,
                                color: subRow.color,
                                position: 'sticky',
                                left: 0,
                                zIndex: 1,
                              }}
                            >
                              <Typography variant="caption" sx={{ mr: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                {subRow.icon}
                            </Typography>
                              <Typography variant="body2" sx={{ color: subRow.color, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                {subRow.label}
                              </Typography>
                            </Box>
                            
                            {dates.map((date) => {
                              const dateStr = date.format('YYYY-MM-DD');
                              const rate = rates[dateStr];
                              const value = subRow.getValue(rate);
                              const isLoading = sourcePending;
                              const isStopSell = !!(rate && (rate.stop_sell || rate.stopSell));
                              const isBlocked = !!(rate && rate.available === false);
                              const hasRate = rate && rate.available !== false && !sourcePending;
                              // For empty values (no rate set), show "-" instead of 0
                              const hasValue = value !== null && value !== undefined && value !== 0;
                              
                              return (
                                <StatusCellTooltip
                                  key={dateStr}
                                  title={getRateCellTooltip(rate)}
                                >
                                <Paper
                                  onClick={() => {
                                    if (isStopSell || isBlocked) {
                                      beginInlineRateEdit({
                                        channelId: selectedChannel === 'all' ? 'pms' : selectedChannel,
                                        date,
                                        roomTypeId: listing.id,
                                        ratePlanId: ratePlanData.rate_plan_id,
                                        currentRate: rate,
                                      });
                                      return;
                                    }
                                    handleDateClick(listing.id, date, null, ratePlanData.rate_plan_id);
                                  }}
                            sx={{
                                    p: { xs: 0.25, sm: 0.5 },
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    border: isLoading
                                      ? `1px solid ${theme.palette.divider}`
                                      : isStopSell
                                      ? `1px solid #c1807b`
                                      : hasRate && hasValue
                                      ? `1px solid #a0c49e`
                                      : `1px solid #e0e0e0`,
                                    borderRadius: isStopSell ? 2 : 1,
                                    backgroundColor: isWeekendDate(date)
                                      ? inventoryWeekendWash(isDark)
                                      : theme.palette.background.paper,
                                    minHeight: { xs: '30px', sm: '35px' },
                                    height: { xs: '30px', sm: '35px' },
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                                    '&:hover': {
                                      backgroundColor: theme.palette.action.hover,
                                    },
                            }}
                          >
                                  {!isLoading && isStopSell && (
                                    <Box sx={inventoryStatusDotSx(isMobileLayout, theme.palette.error.main)} />
                                  )}
                                  {isLoading ? (
                                    <Skeleton variant="rounded" width="66%" height={12} animation="wave" />
                                  ) : (
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        ...rateAmountTextSx,
                                        fontWeight: 600,
                                        color: hasRate && hasValue ? theme.palette.success.main : theme.palette.text.secondary,
                                      }}
                                    >
                                      {hasValue ? formatCurrency(value) : '—'}
                            </Typography>
                        )}
                      </Paper>
                      </StatusCellTooltip>
                    );
                  })}
                </Box>
                        ))}
                      </React.Fragment>
                    );
                  })}
                    </React.Fragment>
                  );
                })
              )
              )
              }
                </Box>
              </Box>
        </Paper>

      <ManageRateUpdateDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setUpdateDrawerContext(null);
        }}
        roomTypes={updateDrawerRooms}
        ratePlans={manageRatePlans}
        isDark={isDark}
        themeColors={themeColors}
        initialContext={updateDrawerContext}
        onSaved={async () => {
          cachedRatesRef.current = {};
          await refreshLoadedRates();
        }}
      />

      <GroupRateUpdateDrawer
        open={groupDrawerOpen}
        onClose={() => setGroupDrawerOpen(false)}
        roomTypes={listings}
        ratePlans={pmsRatePlans.length > 0 ? pmsRatePlans : ratePlans}
        channelManagerRatePlans={channelManagerRatePlans}
        channels={channels}
        isDark={isDark}
        themeColors={themeColors}
        onSaved={async () => {
          await refreshLoadedRates();
        }}
      />

      <GroupRateRestrictionsDrawer
        open={restrictionsDrawerOpen}
        onClose={() => setRestrictionsDrawerOpen(false)}
        roomTypes={listings}
        ratePlans={channelManagerRatePlans}
        channels={channels}
        isDark={isDark}
        themeColors={themeColors}
      />

      <RateStopSellDrawer
        open={stopSellDrawerOpen}
        onClose={() => {
          setStopSellDrawerOpen(false);
          setStopSellInitialContext(null);
        }}
        roomTypes={listings}
        ratePlans={stopSellRatePlans}
        channels={channels}
        isDark={isDark}
        themeColors={themeColors}
        initialContext={stopSellInitialContext}
        platform="both"
        onSaved={async () => {
          await refreshLoadedRates();
        }}
      />

      <RateSyncDrawer
        open={syncDrawerOpen}
        onClose={() => setSyncDrawerOpen(false)}
        roomTypes={listings}
        ratePlans={channelManagerRatePlans}
        channels={channels}
        isDark={isDark}
        themeColors={themeColors}
        onSaved={async () => {
          await refreshLoadedRates();
        }}
      />

      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default ManageRatePage;

